/**
 * KeyAlerts.tsx — the double-prescribing safety banner on the clinical review.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: key alerts (2026-08-07).
 *
 * WHY: a patient reaches a prescriber down three roads — self-serve checkout,
 * clinician callback, and back-office — and each one can mint a plan the other
 * two never see. The person deciding to prescribe must be told, before they
 * decide, that this patient is ALREADY on medication.
 *
 * Deliberate posture:
 *  - STAFF ONLY. The patient lane is never blocked and never told; a "you
 *    already have medication" string is a clinical conversation, not a UI
 *    message. Nothing here is reachable from a patient surface.
 *  - ALERT, never a hard block. Legitimate switches exist (one treatment to
 *    another, one formulation to another), so this component NEVER disables or
 *    gates a prescribe/approve control — it fails OPEN on the action and LOUD
 *    on the display.
 *  - PURE presentational. The read lives in `useMedicationSafety`, owned by the
 *    view that mounts this (the deal drawer, which needs the same answer for
 *    its tab warning, else ReviewView) — matching the estate's "data loading
 *    lives at the view, not the leaf" rule, so switching tabs or re-rendering
 *    the banner never refires the check.
 *
 * THE TYPE IS THE SAFETY CONTROL ({@link MedicationSafety}). A nullable array
 * would collapse "checked, nothing found" and "the check failed" into the same
 * falsy value, and a clinician reading an empty panel concludes "no ongoing
 * medication". On a double-prescribing check, false reassurance is worse than
 * no check at all — so a failed check is its own explicit `unavailable` state
 * that renders an amber "verify manually" warning, and only a genuine `clear`
 * renders nothing.
 *
 * A11y — ONE ASSERTIVE REGION PER SURFACE. The review's documented rule (see
 * the notice comment in ReviewView, and the clinician spec that asserts "no
 * assertive region remains") is that the R31 crisis banner owns the single
 * `role="alert"` on this screen: two assertive regions interrupt each other and
 * a screen-reader user can lose one, and the safeguarding banner must win. So
 * EVERY band here — including the danger one — is a polite `role="status"`.
 * Visual prominence is unchanged (the danger band keeps `tone="danger"`, and it
 * renders first, directly under the crisis banner). Tone is carried by an icon
 * + a worded heading + the plain-English state label, never by colour alone
 * (WCAG 2.1 AA 1.4.1), and the only control (Try again) is a real button.
 */
import type {
  MedicationMatchBasis,
  MedicationMatchConfidence,
  MedicationSafetyItem,
  MedicationSafetyResponse,
  MedicationSafetySubscriptionState,
} from '../api/types';
import { formatUtcDate } from '../lib/format';
import { Alert, Button, Help, WarningIcon } from '../ui/kit';

/**
 * What the review knows about this patient's existing medication.
 *
 * A DISCRIMINATED UNION, never a nullable array — see the module note. Mapped
 * from the wire by {@link medicationSafetyFrom}; `unavailable` is a CLIENT-side
 * state (there is no such status on the wire) meaning "the check did not
 * answer", which is exactly the case the clinician must not mistake for clear.
 *
 * NOTE on `alerts`: the wire's `status` is 'alerts' only when `ongoing` or
 * `recentlyIssued` is non-empty, but this union's `alerts` means "there is
 * something to show", so an in-flight-only answer lands here too and renders at
 * the lower severity. The component decides severity from the ARRAYS, never
 * from a flag.
 */
export type MedicationSafety =
  | { status: 'clear' }
  | {
      status: 'alerts';
      ongoing: MedicationSafetyItem[];
      /** Supply ran out recently — still an alert, softer copy (CTO 2026-08-07). */
      recentlyIssued: MedicationSafetyItem[];
      inFlight: MedicationSafetyItem[];
    }
  | { status: 'unavailable' };

/**
 * The VIEW state of the check, held by ReviewView and rendered by
 * {@link KeyAlertsSection}.
 *
 * WHY this exists rather than `MedicationSafety | null`: a bare null collapses
 * THREE different situations into one value that renders nothing —
 *   1. the check has not answered yet,
 *   2. there is no linked patient, so no check applies (review-only mode),
 *   3. the check FAILED.
 * (1) and (2) genuinely render nothing; (3) MUST render the amber "verify
 * manually" warning. Sharing one falsy value between them is exactly the
 * silent-empty failure this component exists to prevent, and it only takes one
 * mis-ordered `setSafety(null)` to turn a failed check into a clean-looking
 * screen. Naming the states makes that mistake unrepresentable.
 *
 * `loading.previous` is what was already on screen when a re-check started. A
 * RETRY keeps its warning up while the re-check runs — erasing it would leave
 * the clinician a clean screen at the exact moment they are least safe. It is
 * null on a first load (nothing to keep) and deliberately never carried across
 * a change of patient.
 */
export type MedicationSafetyView =
  | { state: 'idle' }
  | { state: 'not-applicable' }
  | { state: 'loading'; previous: MedicationSafety | null }
  | { state: 'ready'; safety: MedicationSafety };

/** The subscription state in words a clinician reads, not a wire enum. */
const SUBSCRIPTION_STATE_LABEL: Record<MedicationSafetySubscriptionState, string> = {
  active: 'active',
  past_due: 'payment overdue',
  dormant: 'not started yet',
};

/**
 * Map the wire answer onto the union.
 *
 * Defensive by design: an answer whose arrays are missing (an older orders
 * revision, a partial body, a proxy that swallowed the payload) is NOT read as
 * "nothing found" — it becomes `unavailable`, because the whole point of the
 * union is that an unanswered check never renders as a clean one.
 *
 * `recentlyIssued` is the ONE exception, and deliberately so: it is an ADDITIVE
 * orders Phase-2 key, so a Phase-1 server answering without it has answered
 * honestly and completely for the tiers it knows about. Demanding it would turn
 * a correct deploy-order (portal ahead of orders) into a screen full of "could
 * not check", which is the same false signal in the other direction. A missing
 * additive array degrades to "no recently-issued tier"; a missing FOUNDING array
 * still means the check did not answer.
 */
export function medicationSafetyFrom(response: MedicationSafetyResponse | null | undefined): MedicationSafety {
  const ongoing = response?.ongoing;
  const inFlight = response?.inFlight;
  if (!Array.isArray(ongoing) || !Array.isArray(inFlight)) return { status: 'unavailable' };
  const recentlyIssued = Array.isArray(response?.recentlyIssued) ? response.recentlyIssued : [];
  if (ongoing.length === 0 && recentlyIssued.length === 0 && inFlight.length === 0) {
    // FORWARD-SKEW FLOOR. Every branch above reads a lane this build knows by
    // name, so a NEWER orders that alerts on a lane added after this build ships
    // would fall through all of them and read as a clean screen — a silent
    // downgrade from "alerts" to "clear", which is the worst outcome this
    // component can produce. The server's own verdict is the backstop: if it
    // says there is something to alert on and we found nothing to show, we did
    // not understand the answer, so say so rather than reassure.
    //
    // This is not hypothetical — it is exactly what `recentlyIssued` did to a
    // Phase-1 portal, and the next lane will do it again.
    if (response?.status === 'alerts') return { status: 'unavailable' };
    // The cross-account half did not finish. The self rows above are empty, so
    // a silent clear would tell the clinician nobody else holds this medicine
    // when that question was never answered. Alerts still show when a lane has
    // rows: those rows were read. An older orders with no coverage field stays
    // a clear, so this build can ship ahead of that field.
    if (response?.crossAccountCoverage === 'partial') return { status: 'unavailable' };
    return { status: 'clear' };
  }
  return { status: 'alerts', ongoing, recentlyIssued, inFlight };
}

/**
 * The at-a-glance flag for a tab that is NOT open — decision 3 (CTO
 * 2026-08-07): a clinician who has not opened the review must still see that
 * something in there needs reading.
 *
 * Returns null when there is nothing to flag. An IN-FLIGHT-only answer is not
 * flagged: the server's own `status` stays 'clear' for it, and it is almost
 * always the very case the clinician is standing on — flagging it would put a
 * warning on nearly every deal and train people to ignore the one that matters.
 *
 * A FAILED check IS flagged. The feature's posture is fail-loud on the display,
 * and a tab that looks unflagged is exactly the false reassurance the whole
 * component exists to prevent — so it gets its own wording, never the wording of
 * a real finding.
 *
 * Mid-retry (`loading` over a previous answer) keeps the previous flag up for
 * the same reason the band itself does: the clinician is least safe at the
 * moment the screen goes quiet.
 */
export function keyAlertFlag(view: MedicationSafetyView): string | null {
  const safety = view.state === 'ready' ? view.safety : view.state === 'loading' ? view.previous : null;
  if (!safety) return null;
  if (safety.status === 'unavailable') return 'Key alert check unavailable';
  if (safety.status === 'clear') return null;
  if (safety.ongoing.length > 0) return 'Key alert — existing medication';
  if (safety.recentlyIssued.length > 0) return 'Key alert — recently issued medication';
  return null;
}

/**
 * The whole key-alerts block, driven by the explicit {@link MedicationSafetyView}.
 *
 * The four states render, in order: nothing (not started), nothing (no linked
 * patient — an amber warning on every order-less review would train clinicians
 * to ignore this component), a quiet pending line on a first check, the
 * PREVIOUS answer while a retry is in flight, and the answer itself.
 *
 * Still pure: the read lives at the view (ReviewView), never here.
 */
export function KeyAlertsSection({ view, onRetry }: { view: MedicationSafetyView; onRetry?: () => void }) {
  if (view.state === 'idle' || view.state === 'not-applicable') return null;

  if (view.state === 'loading') {
    // A RETRY: whatever the clinician was looking at — in practice the amber
    // "could not check" warning — stays on screen until the re-check answers.
    if (view.previous) return <KeyAlerts safety={view.previous} onRetry={onRetry} pending />;
    // A FIRST check: a quiet pending line, so "no banner on screen" is never
    // ambiguous between "checked and clear" and "still checking".
    return <KeyAlertsPending />;
  }

  return <KeyAlerts safety={view.safety} onRetry={onRetry} />;
}

/**
 * The quiet first-load affordance. Deliberately NOT a live region and not an
 * Alert: it is a progress note, not a finding, and it must not add noise to a
 * review that is about to come back clear.
 */
function KeyAlertsPending() {
  return (
    <Help style={{ fontSize: '13.5px', marginBottom: 'var(--gps-s-4)' }}>
      Checking for existing medication…
    </Help>
  );
}

/**
 * The banner. Renders NOTHING when the check came back clear — a clean review
 * gets no extra noise, so the presence of this block always means something.
 *
 * `pending` means a re-check is in flight over this answer: the finding stays
 * exactly where it was and only the retry control reflects the wait.
 */
export function KeyAlerts({
  safety,
  onRetry,
  pending = false,
}: {
  safety: MedicationSafety;
  onRetry?: () => void;
  pending?: boolean;
}) {
  if (safety.status === 'clear') return null;

  if (safety.status === 'unavailable') {
    return (
      <Alert tone="warning" role="status" style={{ marginBottom: 'var(--gps-s-4)' }}>
        <strong style={{ display: 'block', marginBottom: 'var(--gps-s-1)' }}>Key alert check unavailable</strong>
        <p style={{ margin: 0 }}>
          Could not check for existing medication — verify manually before prescribing.
        </p>
        {onRetry ? (
          <Button
            type="button"
            variant="ghost"
            onClick={onRetry}
            disabled={pending}
            style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', marginTop: 'var(--gps-s-2)' }}
          >
            {pending ? 'Checking…' : 'Try again'}
          </Button>
        ) : null}
      </Alert>
    );
  }

  const { ongoing, recentlyIssued, inFlight } = safety;

  return (
    <>
      {/* Polite `role="status"`, NOT `role="alert"` — the R31 crisis banner is
          the single assertive live region on this surface (see the module note
          and the ReviewView notice comment). Prominence is carried visually by
          `tone="danger"` and by rendering first, not by fighting the
          safeguarding banner for the screen reader's attention. */}
      {ongoing.length > 0 ? (
        <Alert tone="danger" role="status" style={{ marginBottom: 'var(--gps-s-4)' }}>
          <strong style={{ display: 'block', marginBottom: 'var(--gps-s-1)' }}>
            Key alert — this patient already has an ongoing medication.
          </strong>
          <MedicationList items={ongoing} label="Ongoing medication for this patient" />
          <p style={{ margin: 'var(--gps-s-2) 0 0' }}>
            Reconfirm the patient&rsquo;s identity before prescribing: check the name, date of birth and postcode in
            the SCR quick-copy block on this screen against the person in front of you.
          </p>
          <p style={{ margin: 'var(--gps-s-2) 0 0' }}>
            Switching treatment is allowed &mdash; but the existing plan must be stopped first. Never leave a patient
            running two plans at once.
          </p>
          <HandoffNote />
        </Alert>
      ) : null}

      {/* GRADED SECOND TIER (CTO 2026-08-07): the paid-for supply has run out,
          but recently enough that the patient may still have medication in hand.
          Still an ALERT — deliberately a softer one, because the honest claim
          here is "they were on this until <date>", not "they are on it now".
          Amber rather than red, and rendered BELOW the danger band when both are
          present: medication the patient is holding outranks medication they
          have just come off. */}
      {recentlyIssued.length > 0 ? (
        <Alert tone="warning" role="status" style={{ marginBottom: 'var(--gps-s-4)' }}>
          <strong style={{ display: 'block', marginBottom: 'var(--gps-s-1)' }}>
            Key alert — this patient was recently issued medication.
          </strong>
          <MedicationList
            items={recentlyIssued}
            label="Recently issued medication for this patient"
            titlePrefix={supplyEndedPrefix}
          />
          <p style={{ margin: 'var(--gps-s-2) 0 0' }}>
            The paid-for supply has run out, but only recently &mdash; the patient may still have medication in hand.
            Ask what they have left before issuing more.
          </p>
          <HandoffNote />
        </Alert>
      ) : null}

      {/* In-flight only: a second journey exists but nothing is being taken yet,
          so it is a heads-up, not the danger alert. Shown BELOW the danger case
          when both are present — the medication the patient is on outranks a
          journey that has not started. */}
      {inFlight.length > 0 ? (
        <Alert tone="info" role="status" style={{ marginBottom: 'var(--gps-s-4)' }}>
          <strong style={{ display: 'block', marginBottom: 'var(--gps-s-1)' }}>
            Another journey is in progress for this patient.
          </strong>
          <MedicationList items={inFlight} label="Journeys in progress for this patient" />
          <p style={{ margin: 'var(--gps-s-2) 0 0' }}>
            Nothing is being dispensed on it yet. Check it is not a duplicate of the case you are reviewing.
          </p>
        </Alert>
      ) : null}
    </>
  );
}

/**
 * The one thing a clinician IS allowed to do about a duplicate (CTO decision 4).
 *
 * A clinician must never stop a patient's plan — that is a money action and it
 * lives with an admin — so the band names the handoff instead of implying a
 * control this screen does not (and must not) have.
 */
function HandoffNote() {
  return (
    <p style={{ margin: 'var(--gps-s-2) 0 0' }}>
      Stopping a plan is an admin action. Use <strong style={{ fontWeight: 600 }}>Send for admin review</strong> on
      this deal to hand it over with a note.
    </p>
  );
}

/**
 * The medications themselves — one readable line each, never a raw payload.
 *
 * `titlePrefix` lets a band state WHEN as well as WHAT on the headline (the
 * recently-issued tier). It is a function of the item so the date can only ever
 * come from the item's own server-computed field.
 */
function MedicationList({
  items,
  label,
  titlePrefix,
}: {
  items: MedicationSafetyItem[];
  label: string;
  titlePrefix?: (item: MedicationSafetyItem) => string | null;
}) {
  return (
    <ul aria-label={label} style={{ margin: 'var(--gps-s-2) 0 0', paddingLeft: 'var(--gps-s-4)' }}>
      {items.map((item) => {
        const prefix = titlePrefix?.(item) ?? null;
        const crossAccount = isCrossAccountMatch(item);
        return (
          <li
            key={item.orderCaseId}
            style={{
              marginBottom: '6px',
              // A cross-account item is set apart VISUALLY as well as in words
              // (WCAG 1.4.1 — the words below carry the meaning on their own).
              ...(crossAccount
                ? {
                    borderLeft: '3px solid currentColor',
                    paddingLeft: 'var(--gps-s-2)',
                    marginLeft: '-3px',
                  }
                : {}),
            }}
          >
            <strong style={{ fontWeight: 600 }}>
              {prefix ? `${prefix} — ` : ''}
              {medicationTitle(item)}
            </strong>
            <span style={{ display: 'block' }}>{medicationDetail(item)}</span>
            {/* The missing dose, stated. See `medicationTitle` for why the
                field that exists is not the field to show. */}
            <span style={{ display: 'block' }}>{NO_RECORDED_DOSE}</span>
            {crossAccount ? <CrossAccountNote item={item} /> : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The recently-issued headline prefix: WHEN the supply ran out.
 *
 * Rendered from the server's `supplyCoveredUntil` and NEVER recomputed here — JS
 * month arithmetic disagrees with the service for every month-end activation,
 * and a safety banner must not state a date the service would dispute. Returns
 * null (so the headline is just the medication) when the item carries no supply
 * window or an unreadable timestamp — a bare "Supply ran out" with no date reads
 * as broken.
 *
 * WORDING (deliberate deviation from the brief's literal "Last issued <date>"):
 * `supplyCoveredUntil` is the END of the paid-for supply, not the instant the
 * medication was issued — on a 3-month plan those are three months apart, so
 * labelling it "last issued" would misdate the last issue by the length of the
 * plan on a clinical safety banner. The band states the same fact truthfully.
 */
function supplyEndedPrefix(item: MedicationSafetyItem): string | null {
  const ended = formatUtcDate(item.supplyCoveredUntil);
  return ended ? `Supply ran out ${ended}` : null;
}

/** Is this item a PROBABLE cross-account match rather than a confirmed one? */
function isCrossAccountMatch(item: MedicationSafetyItem): boolean {
  // Anything that is not the verified same-account match is unconfirmed —
  // including a basis this build has never heard of. Unknown values fall to the
  // WEAKER wording by construction, which is the only safe direction for a
  // union the server may widen ahead of the portal.
  return item.matchedOn !== 'self';
}

/** The confidence band in words a clinician reads, never a raw enum. */
const MATCH_CONFIDENCE_LABEL: Record<MedicationMatchConfidence, string> = {
  certain: 'a confirmed match',
  high: 'a strong match',
  probable: 'a reasonable match',
  possible: 'a weak match',
};

/**
 * How the match was made, and how much to trust it — both in plain English.
 *
 * The server's own `matchConfidence` always wins. It is absent today (orders
 * still emits `matchedOn: 'self'` only), so an unlabelled cross-account basis
 * falls back to a per-basis band. An UNKNOWN basis gets the weakest wording and
 * no invented detail: saying less is safe, overstating a match is not.
 */
const MATCH_BASIS_WORDS: Partial<Record<MedicationMatchBasis, { how: string; confidence: MedicationMatchConfidence }>> = {
  email: { how: 'the same email address on a different account', confidence: 'high' },
  name_dob_address: { how: 'the same name, date of birth and address', confidence: 'probable' },
  name_dob: { how: 'the same name and date of birth', confidence: 'possible' },
};

/**
 * A DIFFERENT NOTATION for a cross-account match (CTO decision 2).
 *
 * This is a PROBABLE identity match, not a confirmed one — it may be the same
 * person under a different email, and it may equally be a different person who
 * shares a name and a date of birth. The item therefore says so in words,
 * states how sure the match is without ever printing the wire enum, and points
 * at the identity block already on this screen. It gates NOTHING.
 */
function CrossAccountNote({ item }: { item: MedicationSafetyItem }) {
  const words = MATCH_BASIS_WORDS[item.matchedOn];
  const confidence = item.matchConfidence ?? words?.confidence ?? 'possible';
  return (
    <span style={{ display: 'block', marginTop: '4px' }}>
      <strong style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 700 }}>
        <WarningIcon size={13} />
        Probable identity match — not confirmed
      </strong>
      <span style={{ display: 'block' }}>
        This is on a DIFFERENT account{words ? `, matched on ${words.how}` : ''} —{' '}
        {MATCH_CONFIDENCE_LABEL[confidence] ?? MATCH_CONFIDENCE_LABEL.possible}.
        It may be the same person under a different email, or someone else entirely.
      </span>
      <span style={{ display: 'block' }}>
        Verify identity before prescribing: check the name, date of birth and address in the SCR quick-copy block on
        this screen against the person in front of you.
      </span>
    </span>
  );
}

/**
 * The medication headline: its name, plus the FORMULATION when the plan carries
 * one. `medicationName` is null on a plan that never resolved one, so the
 * productKey is the fallback — a clinician needs SOMETHING nameable, and an
 * empty line reads as "no medication".
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ⚠️ `item.strength` IS DELIBERATELY NOT RENDERED (2026-09-12). IT USED TO BE,
 * AND THIS WAS THE WORST PLACE IN THE PORTAL FOR IT.
 *
 * orders resolves that field through `PricesPort.describeMedication`, which
 * reads a launch catalogue mirroring the prices-service PLAN ANCHOR — "the
 * dearest variant each family is priced against". It is a COMMERCIAL figure with
 * no clinical provenance whatsoever, and for the device presentations it sits
 * several-fold above the dose treatment starts at.
 *
 * This banner is the highest-consequence read in the estate: it tells a
 * prescriber what the patient is ALREADY ON at the exact moment they choose the
 * next dose. Printing a pricing anchor there, in brackets after the medicine's
 * own name, is indistinguishable from a prescribed dose — and a titration
 * decision made against it is a titration decision made against a price list.
 *
 * `formulation` STAYS. It is a genuine catalogue product fact and carries no
 * dose. The split is the same one the dispensing console and the prescription
 * record now make: product facts may be catalogue-resolved; a clinical value
 * comes from the prescriber or it is stated as absent.
 *
 * AND THE ABSENCE IS SAID OUT LOUD — see `NO_RECORDED_DOSE` below. Quietly
 * dropping the figure would leave a prescriber to assume the system simply had
 * nothing to add, when what it has is nothing TRUSTWORTHY to add. On a
 * concurrent-medication alert, silence about a dose is itself unsafe.
 *
 * TODO(orders): the prescribe form now captures a real prescriber-chosen
 * variant. When this read is repointed at the PRESCRIPTION rather than the
 * catalogue, `strength` becomes a clinical field and the dose can be shown —
 * and this line replaced with it.
 * ═════════════════════════════════════════════════════════════════════════════
 */
function medicationTitle(item: MedicationSafetyItem): string {
  const name = item.medicationName?.trim() || item.productKey;
  const formulation = item.formulation?.trim();
  return formulation ? `${name} (${formulation})` : name;
}

/**
 * What this banner says INSTEAD of a dose.
 *
 * Phrased as a statement about the RECORD, not about the patient: the dose is
 * not unknown to medicine, it is unrecorded by us, and the difference tells the
 * prescriber where to go and get it.
 */
const NO_RECORDED_DOSE = 'No prescribed dose is recorded for this — check the prescription before titrating.';

/**
 * The supporting line: plan month, start date, plain-English subscription state
 * and — only when the server has one — the human `ORD-…` reference.
 *
 * The reference falls back to NOTHING rather than the orderCaseId: a raw UUID
 * on a safety banner is unreadable noise a clinician cannot quote down a phone,
 * and mistaking it for a reference wastes time in the one moment that matters.
 */
function medicationDetail(item: MedicationSafetyItem): string {
  const parts = [
    item.months === null ? `Plan month ${item.monthsElapsed}` : `Plan month ${item.monthsElapsed} of ${item.months}`,
  ];
  // formatUtcDate returns '' for a missing/invalid timestamp — a bare "started"
  // with no date reads as broken, so drop the whole clause instead.
  const started = formatUtcDate(item.startedAt);
  if (started) parts.push(`started ${started}`);
  parts.push(SUBSCRIPTION_STATE_LABEL[item.subscriptionState] ?? item.subscriptionState);
  if (item.reference?.trim()) parts.push(item.reference.trim());
  return parts.join(' · ');
}
